import apiClient from './api-client';
import { 
  GenerateFormUrlRequest, 
  GenerateFormUrlResponse,
  ValidateFormAccessResponse,
  SubmitFormRequest,
  SubmitFormResponse,
  ShareFormRequest,
  ShareFormResponse,
  FormStatusResponse,
  APIResponse 
} from '@/types';

class CheckInFormService {
  private readonly baseEndpoint = '/check-in-form';

  /**
   * Generate a secure check-in form URL for a booking
   */
  async generateFormUrl(request: GenerateFormUrlRequest): Promise<GenerateFormUrlResponse> {
    const response: APIResponse<GenerateFormUrlResponse> = await apiClient.post(
      `${this.baseEndpoint}/generate`,
      request
    );
    return response.data;
  }

  /**
   * Validate form access token and get booking context
   */
  async validateFormAccess(token: string): Promise<ValidateFormAccessResponse> {
    // Make a direct fetch call since this is a public endpoint
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://apis.bnbcart.com';
    const url = `${API_BASE_URL}${this.baseEndpoint}/validate/${token}`;
    
    console.log('Making request to:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      
      // Handle specific error cases
      if (response.status === 401) {
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error?.message?.includes('expired')) {
            const error = new Error('Token expired');
            (error as any).tokenExpired = true;
            throw error;
          }
        } catch (parseError) {
          // If we can't parse the error, treat as invalid token
        }
        throw new Error('Invalid token');
      }
      
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Response data:', data);
    
    return data.data || data;
  }

  /**
   * Submit check-in form data
   */
  async submitForm(token: string, formData: SubmitFormRequest): Promise<SubmitFormResponse> {
    const response: APIResponse<SubmitFormResponse> = await apiClient.post(
      `${this.baseEndpoint}/submit/${token}`,
      formData
    );
    return response.data;
  }

  /**
   * Submit check-in form data (alternative method name for compatibility)
   */
  async submitCheckInForm(
    token: string, 
    formData: any, 
    ipAddress: string, 
    userAgent: string
  ): Promise<any> {
    const submitData = {
      ...formData,
      ipAddress,
      userAgent,
    };
    
    const response: APIResponse<any> = await apiClient.post(
      `${this.baseEndpoint}/submit/${token}`,
      submitData
    );
    return response.data;
  }

  /**
   * Get form status for a booking
   */
  async getFormStatus(bookingId: string): Promise<FormStatusResponse> {
    const response: APIResponse<FormStatusResponse> = await apiClient.get(
      `${this.baseEndpoint}/status/${bookingId}`
    );
    return response.data;
  }

  /**
   * Share form with additional guest
   */
  async shareForm(token: string, shareRequest: ShareFormRequest): Promise<ShareFormResponse> {
    const response: APIResponse<ShareFormResponse> = await apiClient.post(
      `${this.baseEndpoint}/share/${token}`,
      shareRequest
    );
    return response.data;
  }

  /**
   * Validate WhatsApp number format
   */
  validateWhatsAppNumber(number: string): { isValid: boolean; message?: string } {
    // Remove all non-digit characters
    const cleaned = number.replace(/\D/g, '');
    
    if (cleaned.length < 10) {
      return { isValid: false, message: 'WhatsApp number must be at least 10 digits' };
    }
    
    if (cleaned.length > 15) {
      return { isValid: false, message: 'WhatsApp number must be no more than 15 digits' };
    }
    
    // Check if it starts with a valid country code pattern
    const validCountryCodePattern = /^(\+?1|44|33|49|39|34|7|86|91|55|52|54|56|57|58|51|593|594|595|596|597|598|599|502|503|504|505|506|507|508|509|590|591|592|1242|1246|1264|1268|1284|1340|1345|1441|1473|1649|1664|1670|1671|1684|1721|1758|1767|1784|1787|1809|1829|1849|1868|1869|1876|1939|1242|1246|1264|1268|1284|1340|1345|1441|1473|1649|1664|1670|1671|1684|1721|1758|1767|1784|1787|1809|1829|1849|1868|1869|1876|1939)/;
    
    return { isValid: true };
  }

  /**
   * Format WhatsApp number for display
   */
  formatWhatsAppNumber(number: string): string {
    const cleaned = number.replace(/\D/g, '');
    
    // Add + prefix if not present
    if (!number.startsWith('+')) {
      return `+${cleaned}`;
    }
    
    return cleaned;
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): { isValid: boolean; message?: string } {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }
    
    return { isValid: true };
  }

  /**
   * Validate guest name
   */
  validateGuestName(name: string): { isValid: boolean; message?: string } {
    const trimmed = name.trim();
    
    if (trimmed.length < 2) {
      return { isValid: false, message: 'Name must be at least 2 characters long' };
    }
    
    if (trimmed.length > 100) {
      return { isValid: false, message: 'Name must be less than 100 characters' };
    }
    
    // Check for valid characters (letters, spaces, hyphens, apostrophes)
    const nameRegex = /^[a-zA-ZÀ-ÿ\s\-']+$/;
    if (!nameRegex.test(trimmed)) {
      return { isValid: false, message: 'Name can only contain letters, spaces, hyphens, and apostrophes' };
    }
    
    return { isValid: true };
  }

  /**
   * Get form expiration status
   */
  getExpirationStatus(expirationDate: string): {
    isExpired: boolean;
    isExpiringSoon: boolean;
    timeRemaining: string;
  } {
    const now = new Date();
    const expiry = new Date(expirationDate);
    const timeDiff = expiry.getTime() - now.getTime();
    
    const isExpired = timeDiff <= 0;
    const hoursRemaining = Math.floor(timeDiff / (1000 * 60 * 60));
    const isExpiringSoon = hoursRemaining <= 6 && hoursRemaining > 0;
    
    let timeRemaining = '';
    if (isExpired) {
      timeRemaining = 'Expired';
    } else if (hoursRemaining < 1) {
      const minutesRemaining = Math.floor(timeDiff / (1000 * 60));
      timeRemaining = `${minutesRemaining} minutes`;
    } else if (hoursRemaining < 24) {
      timeRemaining = `${hoursRemaining} hours`;
    } else {
      const daysRemaining = Math.floor(hoursRemaining / 24);
      timeRemaining = `${daysRemaining} days`;
    }
    
    return {
      isExpired,
      isExpiringSoon,
      timeRemaining
    };
  }

  /**
   * Generate shareable URL for the form
   */
  generateShareableUrl(token: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/check-in/${token}`;
  }

  /**
   * Copy text to clipboard
   */
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const result = document.execCommand('copy');
        textArea.remove();
        return result;
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }
}

const checkInFormServiceInstance = new CheckInFormService();

export { CheckInFormService };
export const checkInFormService = checkInFormServiceInstance;
export default checkInFormServiceInstance;